import { Link } from "react-router-dom";
import { Section, SectionHeader } from "@/components/ui/Section";
import { Rocket, ShoppingBag, Bot, BarChart3, Network, Building2, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

const audiences = [
  { icon: Rocket, title: "Venture-backed startups", description: "Post-funding clarity on architecture, AI, and execution governance for the next round." },
  { icon: ShoppingBag, title: "Founder-led consumer brands", description: "Commerce, customer intelligence, and operating systems for scaling D2C and lifestyle brands." },
  { icon: Bot, title: "AI-first startups", description: "Workflow design, model orchestration, and platform architecture for AI-native products." },
  { icon: BarChart3, title: "Growth-stage digital businesses", description: "Scalability, observability, and operating cadence for companies past product-market fit." },
  { icon: Network, title: "Operationally complex organizations", description: "Workflow redesign, automation, and cross-system intelligence for multi-function businesses." },
  { icon: Building2, title: "Enterprises exploring AI transformation", description: "Strategy, architecture, and execution partnership for serious AI adoption programs." },
];

export function WhoWeHelpSection() {
  return (
    <Section background="muted">
      <SectionHeader 
        eyebrow="Who we work with"
        variant="dark"
        title="Built for founders at every stage."
        subtitle="Whether you are shaping an idea or scaling a funded company, we meet you where you are."
      />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-14">
        {audiences.map((audience, index) => (
          <motion.div
            key={audience.title}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.07, ease: [0.21, 0.47, 0.32, 0.98] }}
            viewport={{ once: true, margin: "-50px" }}
            className="group relative rounded-2xl p-7 h-full bg-white/[0.04] backdrop-blur-md border border-white/10 hover:border-accent/40 hover:bg-white/[0.07] transition-all duration-500 hover:-translate-y-1.5"
          >
            <div
              aria-hidden="true"
              className="absolute -inset-1 -z-10 opacity-0 group-hover:opacity-100 blur-2xl transition-opacity duration-500"
              style={{ background: "radial-gradient(60% 60% at 50% 0%, hsl(33 100% 47% / 0.30), transparent 70%)" }}
            />
            <div className="w-11 h-11 rounded-xl bg-white/10 border border-white/15 grid place-items-center mb-6 group-hover:bg-accent/20 group-hover:border-accent/40 transition-colors">
              <audience.icon className="h-4 w-4 text-accent" strokeWidth={1.6} />
            </div>
            <h3 className="text-base md:text-lg font-display font-semibold text-white mb-3">{audience.title}</h3>
            <p className="text-sm text-white/65 leading-[1.7]">{audience.description}</p>
          </motion.div>
        ))}
      </div>

      <motion.div 
        className="text-center"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        viewport={{ once: true }}
      >
        <p className="text-white/65 mb-7 text-base md:text-lg">
          Serving India and global founders. Fully remote. Timezone-friendly.
        </p>
        <Link
          to="/who-we-help"
          className="group inline-flex items-center gap-3 rounded-full bg-white text-[#1E1454] pl-3 pr-7 py-2.5 font-medium shadow-[0_22px_50px_-15px_rgba(0,0,0,0.55)] hover:shadow-[0_28px_60px_-18px_rgba(0,0,0,0.7)] transition-all duration-300 hover:-translate-y-0.5"
        >
          <span className="grid place-items-center h-9 w-9 rounded-full bg-[#1E1454] text-white transition-transform duration-300 group-hover:translate-x-0.5">
            <ArrowRight className="h-4 w-4" />
          </span>
          Learn more about who we help
        </Link>
      </motion.div>
    </Section>
  );
}
